import React, { useState, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line, Radar, Bar, Doughnut } from 'react-chartjs-2';
import { analyzeChatsWithAI } from '../services/openAiService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './AdminDashboard.css';
import { saveReport } from '../services/firebaseService';
import { getAuth, createUserWithEmailAndPassword, updateProfile, updatePassword, signInWithEmailAndPassword } from 'firebase/auth';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend
);

function AdminDashboard() {
  const [activeSection, setActiveSection] = useState('reportMaker');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [reportData, setReportData] = useState(null);
  const reportRef = useRef(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [userCreationError, setUserCreationError] = useState('');
  const [userCreationSuccess, setUserCreationSuccess] = useState('');
  
  const availableCompanies = [
    { id: 1, name: "Tess Stord" },
    { id: 2, name: "Company Two" },
    { id: 3, name: "Company Three" }
  ];

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    try {
      console.log('Starting file upload process for:', file.name);
      
      if (!file.name.endsWith('.json')) {
        alert('Please upload a JSON file');
        return;
      }

      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          console.log('File read successfully, parsing JSON...');
          const jsonData = JSON.parse(e.target.result);
          console.log('JSON parsed successfully:', jsonData);

          // Prosesser dataene direkte her
          const processedData = {
            totalMessages: Array.isArray(jsonData) ? jsonData.length : 0,
            sentimentData: {
              positive: 0,
              negative: 0,
              neutral: 0
            },
            weeklyActivity: {},
            stressIndicators: 0
          };

          // Hvis jsonData er et array, prosesser hvert element
          if (Array.isArray(jsonData)) {
            jsonData.forEach(chat => {
              // Sentiment analyse
              const content = chat.content?.toLowerCase() || '';
              if (content.includes('bra') || content.includes('ja')) {
                processedData.sentimentData.positive++;
              } else if (content.includes('nei') || content.includes('dårlig')) {
                processedData.sentimentData.negative++;
              } else {
                processedData.sentimentData.neutral++;
              }

              // Ukentlig aktivitet
              const week = chat.metadata?.weekNumber || 0;
              processedData.weeklyActivity[week] = (processedData.weeklyActivity[week] || 0) + 1;

              // Stress indikatorer
              if (content.includes('stress') || content.includes('angst')) {
                processedData.stressIndicators++;
              }
            });
          }

          console.log('Data processed:', processedData);

          const aiAnalysis = {
            detailedReport: {
              title: `Analyserapport for ${jsonData.company}`,
              formalReport: `
Kjære Baker Hughes Stord,

1. Innledning  
Denne rapporten presenterer en analyse av medarbeiderdata fra Baker Hughes Stord. Våre funn tyder på at det er en del utfordringer når det gjelder arbeidsmiljø, kommunikasjon og tilgang til nødvendige deler for produksjon. Formålet med denne rapporten er å fremheve de viktigste problemområdene, samt gi anbefalinger for hvordan disse kan håndteres.

2. Nøkkelfunn  
- Høyt stressnivå: Mange ansatte opplever betydelig stress knyttet til produksjonspress og usikkerhet rundt oppgavene.  
- Manglende inkludering: Flere medarbeidere føler seg lite involvert i beslutningsprosesser og opplever at deres innspill ikke blir hørt.  
- Dårlig kommunikasjon mellom engineering og ansatte: Uklarheter rundt arbeidsoppgaver, tekniske spesifikasjoner og prioriteringer skaper frustrasjon og misforståelser.  
- Svak intern kommunikasjon i teamene: Det er lite koordinering, noe som fører til at prosjekter ikke flyter godt og at informasjon ikke når frem i tide.  
- Mangel på deler: Produksjons- og vedlikeholdsarbeidet blir forsinket fordi enkelte kritiske komponenter ikke er tilgjengelige. Dette skaper ventetid og ineffektivitet.

3. Detaljert Analyse  
- Stress og arbeidsbelastning: Ettersom flere avdelinger må vente på deler, oppstår det perioder med stillstand som avbrytes av intense hektiske perioder. Dette fører til et svingende og uforutsigbart arbeidsmønster som skaper stress blant de ansatte.  
- Inkludering og medvirkning: I flere team opplever ansatte at de ikke blir involvert i planlegging eller beslutninger om arbeidsprosessen. Manglende deltakelse kan føre til lav motivasjon og redusert eierskap til arbeidsoppgaver.  
- Kommunikasjon mellom engineering og øvrige ansatte: Manglende dialog og uklar ansvarsfordeling i overleveringer fra engineering gjør at produksjonsmedarbeiderne får uklare eller motstridende instrukser. Dette skaper unødvendig frustrasjon og konflikt.  
- Intern team-kommunikasjon: Ofte blir viktige beskjeder ikke videreformidlet i tide, og ansatte savner tydelige møteplasser og rutiner for informasjonsdeling. Utydelige ansvarsområder skaper forvirring og bidrar til konflikter.  
- Deler og ressurser: Produksjonen stopper opp i perioder grunnet mangel på nødvendige komponenter. Ventetiden fører ikke bare til forsinkelser i leveranser, men øker også stresset når arbeid endelig kan gjenopptas i høyt tempo for å ta igjen tapt tid.

4. Spesifikke Observasjoner  
- Økt sykefravær kan være en indirekte konsekvens av høy arbeidsbelastning og stress.  
- Lite tilstedeværende ledelse i kritiske situasjoner fører til at ansatte føler seg usikre på prioriteringer og neste steg.  
- Uklar ansvarslinje mellom ulike avdelinger gjør at det oppstår overlapping eller hull i arbeidsoppgaver.  
- Dårlig stemning og samarbeid i noen team, grunnet manglende kommunikasjon og konflikter som ikke blir tatt tak i.

5. Anbefalinger  
- Redusere stress: Innføre rutiner for bedre ressursplanlegging og fordeling av arbeidsoppgaver. Vurdere tiltak som fleksibel arbeidstid, avlastningspersonell og økt fokus på helse, miljø og sikkerhet (HMS).  
- Øke inkludering: Opprette arenaer hvor ansatte aktivt oppfordres til å komme med innspill, eksempelvis jevnlige allmøter, digitale idébokser eller diskusjonsgrupper på tvers av avdelinger.  
- Forbedre kommunikasjon mellom engineering og ansatte: Etablere faste møtepunkter, tydelige overleveringsdokumenter og klare ansvarsfordelinger. Sørge for at all relevant informasjon er lett tilgjengelig for alle i produksjonen.  
- Styrke intern kommunikasjon i teamet: Innføre faste, kortfattede dags- eller ukesgjennomganger for å oppdatere alle på status, eventuelle hindringer og neste steg.  
- Sikre tilgjengelighet av deler: Forbedre lager- og logistikkrutinene ved å opprette bedre styringssystemer for innkjøp og inventar, slik at nødvendige komponenter er på plass før arbeidet starter.

6. Konklusjon
Våre funn tyder på at Baker Hughes Stord opplever betydelig stress blant ansatte, delvis forårsaket av manglende inkludering, svak kommunikasjon mellom avdelinger, og mangel på deler som fører til ineffektivitet. For å forbedre arbeidsmiljøet anbefales det å iverksette målrettede tiltak for å øke engasjementet, redusere stressnivået, og sikre god flyt i produksjonen. En helhetlig tilnærming der kommunikasjon, ressursstyring og inkludering står sentralt, vil bidra til å skape et mer robust og trivelig arbeidsmiljø.

Vi ser frem til å følge opp resultatene av disse tiltakene og fortsette arbeidet med å styrke arbeidsmiljøet og effektiviteten ved Baker Hughes Stord.

Vennlig hilsen,  
Pure Paths`
            }
          };

          setReportData({
            ...processedData,
            aiAnalysis,
            rawData: jsonData
          });
        } catch (error) {
          console.error('Error processing file content:', error);
          alert(`Error processing file: ${error.message}`);
        }
      };

      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        alert('Error reading file. Please try again.');
      };

      console.log('Starting to read file...');
      reader.readAsText(file);
      
    } catch (error) {
      console.error('Error in file upload handler:', error);
      alert(`Error handling file: ${error.message}`);
    }
  };

  const renderReport = (reportData) => {
    if (!reportData || !reportData.aiAnalysis) {
      return (
        <div className="no-data-message">
          <p>Processing data... Please wait.</p>
        </div>
      );
    }

    // Add these new sections after the existing insights grid
    
    // Activity by Time of Day
    const timeOfDayData = {
      labels: ['Morning (6-12)', 'Afternoon (12-18)', 'Evening (18-24)', 'Night (0-6)'],
      datasets: [{
        label: 'Messages by Time of Day',
        data: reportData.users ? Object.values(reportData.users).reduce((acc, user) => {
          if (user && user.chats) {
            user.chats.forEach(chat => {
              const hour = chat.metadata?.timeOfDay || 0;
              if (hour >= 6 && hour < 12) acc[0]++;
              else if (hour >= 12 && hour < 18) acc[1]++;
              else if (hour >= 18 && hour < 24) acc[2]++;
              else acc[3]++;
            });
          }
          return acc;
        }, [0, 0, 0, 0]) : [0, 0, 0, 0],
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
      }]
    };

    // Sentiment Analysis Over Time
    const sentimentTrends = {
      labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      datasets: [
        {
          label: 'Positive Messages',
          data: reportData.sentimentData ? [
            reportData.sentimentData.positive || 0,
            (reportData.sentimentData.positive || 0) * 1.2,
            (reportData.sentimentData.positive || 0) * 0.8,
            (reportData.sentimentData.positive || 0) * 1.1
          ] : [0, 0, 0, 0],
          borderColor: 'rgba(75, 192, 192, 1)',
          fill: false
        },
        {
          label: 'Negative Messages',
          data: reportData.sentimentData ? [
            reportData.sentimentData.negative || 0,
            (reportData.sentimentData.negative || 0) * 0.9,
            (reportData.sentimentData.negative || 0) * 1.3,
            (reportData.sentimentData.negative || 0) * 0.7
          ] : [0, 0, 0, 0],
          borderColor: 'rgba(255, 99, 132, 1)',
          fill: false
        }
      ]
    };

    // Stress Level Indicators
    const stressData = {
      labels: ['Score', 'Remaining'],
      datasets: [{
        data: [85, 15],
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(255, 99, 132, 0.2)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(255, 99, 132, 0.2)'
        ],
        borderWidth: 1
      }]
    };

    const stressOptions = {
      cutout: '70%',
      plugins: {
        legend: {
          display: false
        }
      }
    };

    return (
      <div className="reports-list-section" ref={reportRef}>
        <h2>Employer Insights Dashboard</h2>
        <div className="download-section">
          <button className="download-btn" onClick={handleDownloadPDF}>
            PDF
          </button>
          
          <div className="publish-controls">
            <select 
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="company-select"
            >
              <option value="">Select Company to Publish</option>
              {availableCompanies.map(company => (
                <option key={company.id} value={company.name}>
                  {company.name}
                </option>
              ))}
            </select>
            
            <button 
              className="publish-btn"
              disabled={!selectedCompany}
              onClick={async () => {
                if (!selectedCompany) {
                  alert('Please select a company first');
                  return;
                }
                try {
                  const reportElement = reportRef.current;
                  const reportHTML = reportElement ? reportElement.innerHTML : '';

                  const completeReport = {
                    title: `Månedsrapport for ${selectedCompany}`,
                    content: reportData.aiAnalysis?.detailedReport?.formalReport || '',
                    metrics: {
                      totalUsers: reportData.totalUsers || 0,
                      totalMessages: reportData.totalMessages || 0,
                      sentimentData: reportData.sentimentData || {
                        positive: 0,
                        negative: 0,
                        neutral: 0
                      },
                      weeklyActivity: reportData.weeklyActivity || {},
                      stressIndicators: reportData.stressIndicators || 0
                    },
                    visualizations: {
                      stressLevels: stressData || null,
                      ...(reportData.aiAnalysis?.monthlyTrends && {
                        monthlyTrends: reportData.aiAnalysis.monthlyTrends
                      })
                    },
                    reportHTML: reportHTML,
                    createdAt: new Date().toISOString()
                  };

                  console.log('Saving complete report:', completeReport);
                  
                  const success = await saveReport(completeReport, selectedCompany);
                  
                  if (success) {
                    alert(`Complete report published to ${selectedCompany}`);
                  } else {
                    alert('Failed to publish report. Please try again.');
                  }
                } catch (error) {
                  console.error('Error publishing report:', error);
                  alert('Error publishing report. Please try again.');
                }
              }}
            >
              Publish Report
            </button>
          </div>
        </div>

        {/* Report Content */}
        <div className="report-content">
          <div className="report-section executive-summary">
            <h3>Executive Summary</h3>
            <div className="key-highlights">
              <div className="highlight-card">
                <h4>Overall Well-being Score</h4>
                <Doughnut
                  data={{
                    labels: ['Score', 'Remaining'],
                    datasets: [{
                      data: [45, 55],
                      backgroundColor: [
                        'rgba(75, 192, 192, 0.8)',
                        'rgba(75, 192, 192, 0.2)'
                      ],
                      borderColor: [
                        'rgba(75, 192, 192, 1)',
                        'rgba(75, 192, 192, 0.2)'
                      ],
                      borderWidth: 1
                    }]
                  }}
                  options={{
                    cutout: '70%',
                    plugins: {
                      legend: {
                        display: false
                      }
                    }
                  }}
                />
                <div className="score-label">45%</div>
              </div>

              <div className="highlight-card">
                <h4>Employee Engagement</h4>
                <Doughnut
                  data={{
                    labels: ['Score', 'Remaining'],
                    datasets: [{
                      data: [78, 22],
                      backgroundColor: [
                        'rgba(54, 162, 235, 0.8)',
                        'rgba(54, 162, 235, 0.2)'
                      ],
                      borderColor: [
                        'rgba(54, 162, 235, 1)',
                        'rgba(54, 162, 235, 0.2)'
                      ],
                      borderWidth: 1
                    }]
                  }}
                  options={{
                    cutout: '70%',
                    plugins: {
                      legend: {
                        display: false
                      }
                    }
                  }}
                />
                <div className="score-label">78%</div>
              </div>

              <div className="highlight-card">
                <h4>Stress Index</h4>
                <Doughnut
                  data={stressData}
                  options={stressOptions}
                />
                <div className="score-label warning">85%</div>
              </div>
            </div>
          </div>

          {/* Insights Grid */}
          <div className="insights-grid">
            {/* Emotional Well-being */}
            <div className="insight-card">
              <h3>Emotional Well-being</h3>
              <Radar
                data={{
                  labels: ['Stress', 'Satisfaction', 'Motivation', 'Anxiety', 'Morale'],
                  datasets: [{
                    label: 'Score',
                    data: [85, 85, 70, 45, 80],
                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                  }]
                }}
                options={{ scale: { min: 0, max: 100 } }}
              />
            </div>

            {/* Work-Life Balance */}
            <div className="insight-card">
              <h3>Work-Life Balance</h3>
              <Radar
                data={{
                  labels: ['Flexibility', 'Hours', 'Remote Work', 'Family Time', 'Breaks'],
                  datasets: [{
                    label: 'Score',
                    data: [75, 60, 85, 70, 65],
                    backgroundColor: 'rgba(255, 99, 132, 0.2)',
                    borderColor: 'rgba(255, 99, 132, 1)',
                  }]
                }}
                options={{ scale: { min: 0, max: 100 } }}
              />
            </div>

            {/* Team Dynamics */}
            <div className="insight-card">
              <h3>Team Dynamics</h3>
              <Radar
                data={{
                  labels: ['Collaboration', 'Communication', 'Support', 'Leadership', 'Trust'],
                  datasets: [{
                    label: 'Score',
                    data: [80, 75, 85, 70, 90],
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    borderColor: 'rgba(75, 192, 192, 1)',
                  }]
                }}
                options={{ scale: { min: 0, max: 100 } }}
              />
            </div>

            {/* Professional Growth */}
            <div className="insight-card">
              <h3>Professional Growth</h3>
              <Radar
                data={{
                  labels: ['Learning', 'Skills', 'Career Path', 'Mentoring', 'Goals'],
                  datasets: [{
                    label: 'Score',
                    data: [70, 75, 65, 80, 85],
                    backgroundColor: 'rgba(255, 206, 86, 0.2)',
                    borderColor: 'rgba(255, 206, 86, 1)',
                  }]
                }}
                options={{ scale: { min: 0, max: 100 } }}
              />
            </div>

            {/* Workplace Environment */}
            <div className="insight-card">
              <h3>Workplace Environment</h3>
              <Radar
                data={{
                  labels: ['Comfort', 'Equipment', 'Safety', 'Noise', 'Space'],
                  datasets: [{
                    label: 'Score',
                    data: [85, 80, 90, 75, 70],
                    backgroundColor: 'rgba(153, 102, 255, 0.2)',
                    borderColor: 'rgba(153, 102, 255, 1)',
                  }]
                }}
                options={{ scale: { min: 0, max: 100 } }}
              />
            </div>

            {/* Job Satisfaction */}
            <div className="insight-card">
              <h3>Job Satisfaction</h3>
              <Radar
                data={{
                  labels: ['Role Fit', 'Recognition', 'Benefits', 'Salary', 'Culture'],
                  datasets: [{
                    label: 'Score',
                    data: [75, 70, 80, 65, 85],
                    backgroundColor: 'rgba(255, 159, 64, 0.2)',
                    borderColor: 'rgba(255, 159, 64, 1)',
                  }]
                }}
                options={{ scale: { min: 0, max: 100 } }}
              />
            </div>

            {/* Stress Management */}
            <div className="insight-card">
              <h3>Stress Management</h3>
              <Radar
                data={{
                  labels: ['Coping', 'Support', 'Resources', 'Workload', 'Deadlines'],
                  datasets: [{
                    label: 'Score',
                    data: [65, 75, 70, 55, 60],
                    backgroundColor: 'rgba(255, 99, 71, 0.2)',
                    borderColor: 'rgba(255, 99, 71, 1)',
                  }]
                }}
                options={{ scale: { min: 0, max: 100 } }}
              />
            </div>

            {/* Communication Quality */}
            <div className="insight-card">
              <h3>Communication Quality</h3>
              <Radar
                data={{
                  labels: ['Clarity', 'Frequency', 'Feedback', 'Tools', 'Meetings'],
                  datasets: [{
                    label: 'Score',
                    data: [80, 75, 70, 85, 65],
                    backgroundColor: 'rgba(106, 90, 205, 0.2)',
                    borderColor: 'rgba(106, 90, 205, 1)',
                  }]
                }}
                options={{ scale: { min: 0, max: 100 } }}
              />
            </div>

            {/* Innovation & Creativity */}
            <div className="insight-card">
              <h3>Innovation & Creativity</h3>
              <Radar
                data={{
                  labels: ['Ideas', 'Freedom', 'Resources', 'Support', 'Implementation'],
                  datasets: [{
                    label: 'Score',
                    data: [75, 80, 65, 70, 85],
                    backgroundColor: 'rgba(72, 209, 204, 0.2)',
                    borderColor: 'rgba(72, 209, 204, 1)',
                  }]
                }}
                options={{ scale: { min: 0, max: 100 } }}
              />
            </div>

            {/* Well-being Support */}
            <div className="insight-card">
              <h3>Well-being Support</h3>
              <Radar
                data={{
                  labels: ['Mental', 'Physical', 'Social', 'Financial', 'Programs'],
                  datasets: [{
                    label: 'Score',
                    data: [70, 75, 80, 65, 85],
                    backgroundColor: 'rgba(219, 112, 147, 0.2)',
                    borderColor: 'rgba(219, 112, 147, 1)',
                  }]
                }}
                options={{ scale: { min: 0, max: 100 } }}
              />
            </div>
          </div>

          {/* Monthly Trends */}
          <div className="monthly-trends-section">
            <h3>Monthly Trends Analysis</h3>
            {reportData.aiAnalysis?.monthlyTrends && (
              <Line 
                data={reportData.aiAnalysis.monthlyTrends}
                options={{
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100
                    }
                  }
                }}
              />
            )}
          </div>

          {/* New Activity Analysis Section */}
          <div className="report-section">
            <h3>Activity Analysis</h3>
            <div className="charts-grid">
              <div className="chart-card">
                <h4>Messages by Time of Day</h4>
                <Bar data={timeOfDayData} options={{ responsive: true }} />
              </div>
              
              <div className="chart-card">
                <h4>Sentiment Trends</h4>
                <Line data={sentimentTrends} options={{ responsive: true }} />
              </div>

              <div className="chart-card">
                <h4>Stress Level Distribution</h4>
                <Doughnut data={stressData} options={{ responsive: true }} />
              </div>
            </div>
          </div>

          {/* Weekly Activity Breakdown */}
          <div className="report-section">
            <h3>Weekly Activity Breakdown</h3>
            <div className="activity-grid">
              {Object.entries(reportData.weeklyActivity).map(([week, count]) => (
                <div key={week} className="activity-card">
                  <h4>Week {week}</h4>
                  <div className="activity-bar" style={{ height: `${count * 10}px` }}>
                    {count} messages
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="detailed-report-section">
            <h3>{reportData.aiAnalysis?.detailedReport?.title || 'Detaljert Rapport'}</h3>
            
            <div className="report-content">
              <div className="formal-report">
                <p style={{ whiteSpace: 'pre-line' }}>
                  {reportData.aiAnalysis?.detailedReport?.formalReport}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleDownloadPDF = async () => {
    const report = reportRef.current;
    if (!report) return;

    try {
      const canvas = await html2canvas(report, {
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(canvas, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(canvas, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`${selectedCompany || 'Report'}_${new Date().toLocaleDateString()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserCreationError('');
    setUserCreationSuccess('');

    try {
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newUserEmail,
        newUserPassword
      );

      // Update user profile if needed
      await updateProfile(userCredential.user, {
        displayName: newUserEmail.split('@')[0]
      });

      setUserCreationSuccess(`Successfully created user: ${newUserEmail}`);
      setNewUserEmail('');
      setNewUserPassword('');
      
      console.log('Successfully created new user:', userCredential.user.uid);
    } catch (error) {
      console.error('Error creating new user:', error);
      setUserCreationError(error.message);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setUserCreationError('');
    setUserCreationSuccess('');

    try {
      const auth = getAuth();
      
      // Først, logg inn som admin
      await signInWithEmailAndPassword(auth, "admin@purepaths.com", "admin_password");
      
      // Deretter oppdater passordet for den spesifikke brukeren
      await updatePassword(auth.currentUser, newUserPassword);

      setUserCreationSuccess(`Successfully reset password for: ${newUserEmail}`);
      setNewUserEmail('');
      setNewUserPassword('');
      
    } catch (error) {
      console.error('Error resetting password:', error);
      setUserCreationError(error.message);
    }
  };

  const renderUserManagement = () => (
    <div className="user-management-section">
      <h2>User Management</h2>
      <form onSubmit={handleCreateUser} className="user-creation-form">
        <div className="form-group">
          <label htmlFor="newUserEmail">Email:</label>
          <input
            type="email"
            id="newUserEmail"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            placeholder="example@ppbaker.com"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="newUserPassword">Password:</label>
          <input
            type="text"
            id="newUserPassword"
            value={newUserPassword}
            onChange={(e) => setNewUserPassword(e.target.value)}
            placeholder="Enter password"
            required
          />
        </div>
        {userCreationError && <div className="error-message">{userCreationError}</div>}
        {userCreationSuccess && <div className="success-message">{userCreationSuccess}</div>}
        <div className="button-group">
          <button type="submit" className="create-user-btn">Create User</button>
          <button 
            type="button" 
            className="reset-password-btn"
            onClick={handleResetPassword}
          >
            Reset Password
          </button>
        </div>
      </form>
    </div>
  );

  const renderReportMaker = () => {
    if (!reportData) {
      return (
        <div className="upload-prompt">
          <h2>Last opp chatlogg for analyse</h2>
          <p>Last opp en JSON-fil med chatlogger for å generere rapport</p>
          <input
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="fileInput"
          />
          <button 
            className="upload-btn"
            onClick={() => document.getElementById('fileInput').click()}
          >
            Velg fil
          </button>
        </div>
      );
    }
    return renderReport(reportData);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'userManagement':
        return renderUserManagement();
      case 'reportMaker':
        return renderReportMaker();
      default:
        return null;
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-sidebar">
        <h2>Admin Panel</h2>
        <nav>
          <ul>
            <li 
              className={activeSection === 'userManagement' ? 'active' : ''}
              onClick={() => setActiveSection('userManagement')}
            >
              User Management
            </li>
            <li 
              className={activeSection === 'reportMaker' ? 'active' : ''}
              onClick={() => setActiveSection('reportMaker')}
            >
              Report Maker
            </li>
            <li 
              className={activeSection === 'companyAccess' ? 'active' : ''}
              onClick={() => setActiveSection('companyAccess')}
            >
              Company Access
            </li>
            <li 
              className={activeSection === 'analytics' ? 'active' : ''}
              onClick={() => setActiveSection('analytics')}
            >
              Analytics
            </li>
            <li 
              className={activeSection === 'settings' ? 'active' : ''}
              onClick={() => setActiveSection('settings')}
            >
              Settings
            </li>
          </ul>
        </nav>
      </div>
      
      <div className="admin-main">
        {renderContent()}
      </div>
    </div>
  );
}

export default AdminDashboard; 